import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet as WalletIcon,
  Lock,
  ShieldCheck,
  Search,
  Copy,
  Check,
  Database,
  TrendingUp,
  RefreshCw,
  Coins,
} from "lucide-react";
import LiveTicker from "../components/aegis/LiveTicker";
import { getAegisWalletsDirectory } from "../lib/api";

function formatProof(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return Math.round(x).toLocaleString();
}

function formatApt(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000) return `${(x / 1_000).toFixed(2)}k`;
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CopyAddress({ value }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={`copy-${value}`}
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
      className="inline-flex items-center gap-1 text-zinc-500 hover:text-amber-300 text-[10px] uppercase tracking-[0.2em] font-mono"
      title={done ? "copied" : "copy address"}
    >
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function KindChip({ kind, locked }) {
  if (kind === "liquidity_vault") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 bg-sky-500/5">
        <Lock className="h-3 w-3" /> Locked Vault
      </span>
    );
  }
  if (kind === "payout_pool") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-violet-500/40 text-violet-300 bg-violet-500/5">
        <Database className="h-3 w-3" /> Payout Pool
      </span>
    );
  }
  if (kind === "user") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
        <WalletIcon className="h-3 w-3" /> User
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-400 bg-zinc-900/40">
      {kind}
    </span>
  );
}

function WalletRow({ w, multiplier }) {
  const effectiveApt = w.effective_apt ?? Number((w.total_apt * (multiplier || 1)).toFixed(2));
  const hasUplift = (multiplier || 1) > 1.0001;
  const accent =
    w.kind === "liquidity_vault"
      ? "border-sky-500/40 bg-sky-500/[0.04]"
      : w.kind === "payout_pool"
      ? "border-violet-500/40 bg-violet-500/[0.04]"
      : w.is_architect
      ? "border-amber-500/40 bg-amber-500/[0.04]"
      : "border-zinc-800 bg-[#0b0b0d]";
  return (
    <article
      data-testid={`wallet-row-${w.wallet_id}`}
      className={`border ${accent} hover:border-amber-500/50 transition-colors`}
    >
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 p-3 sm:p-4 items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-heading text-base tracking-[0.05em] text-zinc-100 truncate">
              {w.handle}
            </div>
            <KindChip kind={w.kind} locked={w.locked} />
            {w.is_architect && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 bg-amber-500/5">
                <ShieldCheck className="h-3 w-3" /> Architect
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
            <span
              className="text-zinc-400 truncate max-w-[280px] sm:max-w-[420px]"
              title={w.public_address}
              data-testid={`addr-${w.wallet_id}`}
            >
              {w.public_address}
            </span>
            <CopyAddress value={w.public_address} />
            <span>· △ {w.balance} coins</span>
            <span className="text-amber-200/60" title={`${(w.total_proof || 0).toLocaleString()} PROOF backing`}>
              · ◆ {formatProof(w.total_proof)} PROOF
            </span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div
            className="font-mono text-2xl text-amber-300 leading-none tabular-nums"
            title={`${(w.total_apt || 0).toFixed(2)} APT intrinsic · ${effectiveApt.toFixed(2)} APT effective`}
          >
            {formatApt(hasUplift ? effectiveApt : w.total_apt)}
          </div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1 font-mono">
            {hasUplift ? "APT (effective)" : "APT"}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Wallets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAegisWalletsDirectory(200)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e?.message || String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const wallets = data?.wallets || [];
  const multiplier = data?.network?.burn_multiplier || 1;
  const shards = data?.shards || {};

  const filtered = useMemo(() => {
    let rows = wallets;
    if (filter !== "all") rows = rows.filter((w) => w.kind === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter(
        (w) =>
          (w.handle || "").toLowerCase().includes(needle) ||
          (w.wallet_id || "").toLowerCase().includes(needle)
      );
    }
    return rows;
  }, [wallets, filter, q]);

  const counts = useMemo(() => {
    const c = { all: wallets.length, liquidity_vault: 0, payout_pool: 0, user: 0, system_other: 0 };
    for (const w of wallets) c[w.kind] = (c[w.kind] || 0) + 1;
    return c;
  }, [wallets]);

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <LiveTicker fallbackMultiplier={multiplier} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-1 inline-flex items-center gap-2">
              <Database className="h-3 w-3" /> △ AEGIS · public wallet directory
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
              Wallets
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Every AEGIS wallet exposes a{" "}
              <span className="text-amber-300 font-mono">public address</span>{" "}
              and its on-chain balance — anyone can search and audit.
              <span className="text-rose-300"> No endpoint ever returns auth credentials</span>:
              the Architect&apos;s wallet appears under the exact same public view
              as everyone else, and their login key can only touch the 9%
              treasury slice. The 51% backing is sharded across{" "}
              <span className="text-sky-300 font-mono">{shards.liquidity_vault_count || 7}</span>{" "}
              locked vaults so a compromise of any single shard cannot drain
              the entire liquidity pool.
            </p>
          </div>
          <button
            onClick={() => setTick((t) => t + 1)}
            data-testid="wallets-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-amber-500/60 hover:text-amber-300"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Search + filter */}
        <div className="border border-zinc-800 bg-[#0b0b0d] p-3 mb-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600 pointer-events-none" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search address or handle…"
                data-testid="wallets-search"
                className="w-full pl-9 pr-3 py-2 bg-black border border-zinc-800 text-zinc-200 placeholder-zinc-600 text-sm font-mono focus:outline-none focus:border-amber-500/60"
              />
            </div>
            {[
              ["all", `All · ${counts.all || 0}`, null],
              ["liquidity_vault", `Vaults · ${counts.liquidity_vault || 0}`, "sky"],
              ["payout_pool", `Payouts · ${counts.payout_pool || 0}`, "violet"],
              ["user", `Users · ${counts.user || 0}`, "emerald"],
            ].map(([key, label]) => {
              const active = filter === key;
              return (
                <button
                  key={key}
                  data-testid={`wallets-filter-${key}`}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-mono border ${
                    active
                      ? "border-amber-500/60 text-amber-300 bg-amber-500/5"
                      : "border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-200"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
          <div className="border border-amber-500/40 bg-amber-500/5 p-3" data-testid="stat-total-apt">
            <div className="text-[9px] uppercase tracking-[0.3em] text-amber-400 mb-1 font-mono">
              Total APT
            </div>
            <div className="font-mono text-xl text-amber-300 tabular-nums">
              {formatApt(data?.network?.total_minted_apt || 0)}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-amber-200/60 mt-1 font-mono" title="LLM tokens backing every APT (1 APT = 1,000 PROOF)">
              ◆ {formatProof(data?.network?.total_minted_proof || 0)} PROOF backing
            </div>
          </div>
          <div className="border border-sky-500/40 bg-sky-500/5 p-3" data-testid="stat-vaults">
            <div className="text-[9px] uppercase tracking-[0.3em] text-sky-400 mb-1 font-mono">
              Locked vaults
            </div>
            <div className="font-mono text-xl text-sky-300 tabular-nums">
              {counts.liquidity_vault || 0}{" "}
              <span className="text-zinc-500 text-xs">/ {shards.liquidity_vault_count || 7}</span>
            </div>
          </div>
          <div className="border border-violet-500/40 bg-violet-500/5 p-3" data-testid="stat-payout">
            <div className="text-[9px] uppercase tracking-[0.3em] text-violet-400 mb-1 font-mono">
              Payout pools
            </div>
            <div className="font-mono text-xl text-violet-300 tabular-nums">
              {counts.payout_pool || 0}
            </div>
          </div>
          <div className="border border-emerald-500/40 bg-emerald-500/5 p-3 col-span-3 sm:col-span-1" data-testid="stat-users">
            <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-400 mb-1 font-mono">
              User wallets
            </div>
            <div className="font-mono text-xl text-emerald-300 tabular-nums">
              {counts.user || 0}
            </div>
          </div>
        </div>

        {/* List */}
        {loading && !data && (
          <div className="text-zinc-500 text-sm py-12 text-center font-mono uppercase tracking-[0.25em]" data-testid="wallets-loading">
            Loading directory…
          </div>
        )}
        {error && (
          <div className="text-rose-400 text-sm py-6 border border-rose-500/40 bg-rose-500/5 px-4 font-mono" data-testid="wallets-error">
            {String(error)}
          </div>
        )}
        {!loading && filtered.length === 0 && !error && (
          <div className="text-zinc-500 text-sm py-16 text-center border border-zinc-800 bg-[#0b0b0d]" data-testid="wallets-empty">
            No wallets match your filter.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="space-y-2" data-testid="wallets-list">
            {filtered.map((w) => (
              <WalletRow key={w.wallet_id} w={w} multiplier={multiplier} />
            ))}
          </div>
        )}

        {/* Security footer */}
        <div className="mt-10 pt-6 border-t border-zinc-900 text-xs text-zinc-500 leading-relaxed">
          <div className="inline-flex items-start gap-2 max-w-3xl">
            <Lock className="h-4 w-4 text-zinc-600 mt-0.5 shrink-0" />
            <div>
              <span className="text-zinc-300 font-mono uppercase tracking-[0.2em] text-[10px]">Security model:</span>{" "}
              {data?.security_notes ||
                "Public addresses, balances and history are searchable; private credentials never are."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
